import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { useCommonUI } from "../contexts/CommonUIContext";
import TopBar from "../components/TopBar";
import DropdownMenu from "../components/DropdownMenu";
import ProjectChip from "../components/ProjectChip";
import NotificationBell from "../components/NotificationBell";
import AppInfoDialog from "../components/AppInfoDialog";
import accountIcon from "../assets/icon_account.svg";
import infoIcon from "../assets/icon_info.svg";
import logoutIcon from "../assets/icon_logout.svg";
import folderAddIcon from "../assets/icon_folder_add.svg";
import folderOpenIcon from "../assets/icon_folder_open.svg";
import Colors from "../constants/Colors";
import { isAllProjects } from "../constants/projects";
import { recurrenceSummary, formatDateTime } from "../utils/recurrence";

const DANGER = "#e5484d";
const SUCCESS = "#2e9e5b";

function RecurringScreen() {
  const navigate = useNavigate();
  const {
    firebaseAuth,
    projects,
    activeProject,
    handleProjectChange,
    recurringRules,
    createRecurringRule,
    toggleRecurringRule,
    createNewProject,
  } = useContext(FirebaseContext);
  const { openModal } = useCommonUI();

  const handleLogout = async () => {
    try {
      await signOut(firebaseAuth);
      navigate("/auth");
    } catch (e) {
      console.error("Sign out failed:", e);
    }
  };

  const accountMenuItems = [
    { icon: infoIcon, label: "App Information", onClick: () => openModal(<AppInfoDialog />) },
    { icon: logoutIcon, label: "Sign Out", color: DANGER, onClick: handleLogout },
  ];

  const projectActionItems = [
    {
      icon: folderAddIcon,
      label: "Add Project",
      onClick: async () => {
        const id = await createNewProject();
        if (id) navigate(`/projectDetail/${id}`);
      },
    },
  ];
  if (activeProject && !isAllProjects(activeProject.id)) {
    projectActionItems.push({
      icon: folderOpenIcon,
      label: `Edit ${activeProject.projectName}`,
      onClick: () => navigate(`/projectDetail/${activeProject.id}`),
    });
  }

  // Edit on a dedicated full screen (proper context + scroll), not a modal.
  const openEditor = (rule) => {
    navigate(`/recurringDetail/${rule.id}`, { state: { rule } });
  };

  const handleCreate = async () => {
    const rule = await createRecurringRule();
    if (rule) openEditor(rule);
  };

  const styles = {
    container: { paddingTop: "66px", height: "calc(100vh - 60px)", boxSizing: "border-box" },
    body: { padding: "16px", height: "100%", overflowY: "auto", boxSizing: "border-box" },
    header: { fontWeight: 700, fontSize: "13px", color: "var(--text-muted, #888)", margin: "4px 0 12px" },
    empty: { color: "var(--text-muted, #888)", fontSize: "14px" },
    fab: {
      position: "fixed",
      bottom: "72px",
      right: "16px",
      backgroundColor: Colors.primary,
      color: "#fff",
      border: "none",
      borderRadius: "50%",
      width: "56px",
      height: "56px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      fontSize: "24px",
      cursor: "pointer",
      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
      zIndex: 1000,
    },
  };

  return (
    <div style={styles.container}>
      <TopBar
        leftContainer={
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <DropdownMenu icon={accountIcon} menuItems={accountMenuItems} />
            <NotificationBell />
          </div>
        }
        rightContainer={
          <ProjectChip
            projects={projects}
            activeProject={activeProject}
            onProjectChange={handleProjectChange}
            actionItems={projectActionItems}
          />
        }
      />
      <div style={styles.body}>
        <div style={styles.header}>RECURRING TASKS</div>
        {recurringRules.length === 0 && (
          <div style={styles.empty}>No recurring rules yet. Tap + to add one.</div>
        )}
        {recurringRules.map((rule) => (
          <RuleCard
            key={rule.id}
            rule={rule}
            onOpen={() => openEditor(rule)}
            onToggle={() => toggleRecurringRule(rule)}
          />
        ))}
      </div>
      <button style={styles.fab} onClick={handleCreate} aria-label="Add recurring rule">
        +
      </button>
    </div>
  );
}

function RuleCard({ rule, onOpen, onToggle }) {
  const styles = {
    card: {
      background: "var(--surface)",
      color: "var(--text)",
      borderRadius: "10px",
      padding: "14px",
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
    },
    title: { fontWeight: 600 },
    sub: { fontSize: "12px", color: "var(--text-muted, #888)" },
    next: { fontSize: "12px", color: rule.enabled ? SUCCESS : "var(--text-muted, #888)" },
    toggle: {
      background: "none",
      border: "none",
      color: Colors.primary,
      cursor: "pointer",
      fontSize: "14px",
      padding: "4px 8px",
    },
  };
  return (
    <div style={styles.card}>
      <div style={{ flex: 1 }} onClick={onOpen}>
        <div style={styles.title}>{rule.title || "Untitled rule"}</div>
        <div style={styles.sub}>{recurrenceSummary(rule)}</div>
        <div style={styles.next}>
          {rule.enabled ? `Next: ${formatDateTime(rule.nextFireAt)}` : "Paused"}
        </div>
      </div>
      <button style={styles.toggle} onClick={onToggle}>
        {rule.enabled ? "Pause" : "Activate"}
      </button>
    </div>
  );
}

export default RecurringScreen;

